import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserContext } from '../../contexts';
import { meFn } from '../../lib/api';
import config from '../../lib/config';
import { getPersistedValue } from '../../lib/utils';
import { getNativeAppClient, NativeAppHandoff } from '../NativeAppHandoff';

const DEFAULT_BUNDLE_DOWNLOAD_URL = 'https://www.productioncrate.com/portal';
const STARTUP_TIMER_SECONDS = 5;
const REFRESH_TOKEN_LOOKUP_ATTEMPTS = 20;
const REFRESH_TOKEN_LOOKUP_INTERVAL_MS = 200;
const PORTAL_SCHEME_FALLBACK_DELAY_MS = 1200;
const GENERIC_FEEDBACK_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSfPp8q1Bj22OaP4qKmsIWzg11UrtkG2Kn0N9La0dQ_DIsaq1A/formResponse';
const SURVEY_NAME_ID = 'entry.1864451892';
const QUESTION_IDS = [
  { questionId: 'entry.1982830785', answerId: 'entry.1053492427' },
  { questionId: 'entry.57395573', answerId: 'entry.686072284' },
  { questionId: 'entry.412179655', answerId: 'entry.2009039441' },
  { questionId: 'entry.837359487', answerId: 'entry.2143974432' },
  { questionId: 'entry.590641382', answerId: 'entry.661817722' }
];
const USER_IDS = {
  username: 'entry.674977585',
  email: 'entry.790349041',
  plan_id: 'entry.1849155168'
};
const SURVEY_SOURCE_VALUE_BY_LABEL = {
  'Google search': 'google_search',
  Instagram: 'instagram',
  Facebook: 'facebook',
  YouTube: 'youtube',
  'From a Friend': 'from_a_friend',
  Other: 'other',
};

const wait = (durationMs) => new Promise((resolve) => setTimeout(resolve, durationMs));

const isWindowsClient = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const platformSignals = [
    navigator.userAgentData?.platform,
    navigator.platform,
    navigator.userAgent
  ]
    .filter((signal) => typeof signal === 'string' && signal.trim())
    .join(' ')
    .toLowerCase();

  return platformSignals.includes('win');
};

const getPersistedRefreshToken = () => {
  const refreshTokenValue = getPersistedValue(
    config.refreshTokenLocalStorageKey,
    config.refreshTokenCookieKey
  );
  if (typeof refreshTokenValue !== 'string') {
    return null;
  }
  const trimmedRefreshToken = refreshTokenValue.trim();
  return trimmedRefreshToken || null;
};

const resolveRefreshTokenFn = async ({ isCancelled } = {}) => {
  let refreshTokenFn = typeof window.refreshToken === 'function'
    ? window.refreshToken
    : null;

  if (refreshTokenFn) {
    return refreshTokenFn;
  }

  for (let attempt = 0; attempt < REFRESH_TOKEN_LOOKUP_ATTEMPTS; attempt += 1) {
    if (typeof isCancelled === 'function' && isCancelled()) {
      return null;
    }
    await wait(REFRESH_TOKEN_LOOKUP_INTERVAL_MS);
    if (typeof window.refreshToken === 'function') {
      refreshTokenFn = window.refreshToken;
      break;
    }
  }

  return typeof refreshTokenFn === 'function' ? refreshTokenFn : null;
};

const refreshSessionAndGetRefreshToken = async ({ isCancelled } = {}) => {
  const refreshTokenFn = await resolveRefreshTokenFn({ isCancelled });
  if (typeof refreshTokenFn !== 'function') {
    return getPersistedRefreshToken();
  }

  try {
    await refreshTokenFn();
  } catch (error) {
    console.log('Error while refreshing token for bundle checkout result', error);
  }

  return getPersistedRefreshToken();
};

const toSafeHttpUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  try {
    const parsedUrl = new URL(value.trim(), window.location.origin);
    if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
      return parsedUrl.toString();
    }
  } catch (error) {
    return null;
  }

  return null;
};

const parsePositiveInteger = (value) => {
  const parsedValue = Number(value);
  if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
    return null;
  }
  return parsedValue;
};

const resolveSurveySourceValue = (label) => {
  if (!label) {
    return 'skipped';
  }
  return SURVEY_SOURCE_VALUE_BY_LABEL[label] || 'other';
};

const toSettingsPayload = ({ source, unlockMode, bundleId, redirectUrl }) => {
  const payload = {
    bundle_success_find_us_source: source,
    bundle_success_find_us_unlock_mode: unlockMode,
    bundle_success_find_us_at: `${Date.now()}`,
  };

  if (bundleId) {
    payload.bundle_success_find_us_bundle_id = String(bundleId);
  }
  if (redirectUrl) {
    payload.bundle_success_find_us_redirect_url = redirectUrl;
  }

  return payload;
};

const submitBundleSuccessSurveyToGoogle = ({ source, unlockMode, bundleId, redirectUrl, user }) => {
  const formData = new FormData();
  formData.append(SURVEY_NAME_ID, 'bundle_success_find_us_poc');

  formData.append(QUESTION_IDS[0].questionId, 'find_us_source');
  formData.append(QUESTION_IDS[0].answerId, source);

  formData.append(QUESTION_IDS[2].questionId, 'unlock_mode');
  formData.append(QUESTION_IDS[2].answerId, unlockMode);

  if (redirectUrl) {
    formData.append(QUESTION_IDS[3].questionId, 'redirect_url');
    formData.append(QUESTION_IDS[3].answerId, redirectUrl);
  }

  if (bundleId) {
    formData.append(USER_IDS.plan_id, String(bundleId));
  }
  if (typeof user?.username === 'string' && user.username.trim()) {
    formData.append(USER_IDS.username, user.username.trim());
  }
  if (typeof user?.email === 'string' && user.email.trim()) {
    formData.append(USER_IDS.email, user.email.trim());
  }

  return fetch(GENERIC_FEEDBACK_FORM_URL, {
    method: 'POST',
    body: formData,
    mode: 'no-cors',
    keepalive: true,
  });
};

const FIND_US_OPTIONS = [
  { label: 'Google search', icon: 'google' },
  { label: 'Instagram', icon: 'instagram' },
  { label: 'Facebook', icon: 'facebook' },
  { label: 'YouTube', icon: 'youtube' },
  { label: 'From a Friend', icon: 'friends' },
  { label: 'Other', icon: null },
];

const OptionLogo = ({ icon }) => {
  if (icon === 'google') {
    return (
      <img src="/images/logo.google.favicon.2025.svg" alt="" aria-hidden="true" style={{width: '24px', height: '24px', display: 'block'}} />
    );
  }

  if (icon === 'instagram') {
    return (
      <img src="/images/logo.instagram.2022.svg" alt="" aria-hidden="true" style={{width: '24px', height: '24px', display: 'block'}} />
    );
  }

  if (icon === 'youtube') {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="2.3" y="5.2" width="19.4" height="13.6" rx="4.2" fill="#FF0000" />
        <path d="M10 9.1L15.6 12L10 14.9V9.1Z" fill="#FFFFFF" />
      </svg>
    );
  }

  if (icon === 'facebook') {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill="#1877F2" />
        <path d="M13.64 18v-5.47h1.84l.28-2.13h-2.12V9.04c0-.62.17-1.04 1.06-1.04h1.13V6.09C15.64 6.06 14.96 6 14.18 6c-1.63 0-2.75 1-2.75 2.82v1.58H9.58v2.13h1.85V18h2.21z" fill="#FFFFFF" />
      </svg>
    );
  }

  if (icon === 'friends') {
    return (
      <span aria-hidden="true" style={{fontSize: '22px', lineHeight: 1}}>
        👥
      </span>
    );
  }

  return null;
};

const BundleCheckoutSuccessPoc = () => {
  const { user } = useUserContext();
  const [searchParams] = useSearchParams();
  const nativeAppClient = getNativeAppClient(searchParams);
  const [selectedSource, setSelectedSource] = useState('');
  const [remainingSeconds, setRemainingSeconds] = useState(STARTUP_TIMER_SECONDS);
  const [isLaunchingPortal, setIsLaunchingPortal] = useState(false);
  const [isCtaPressed, setIsCtaPressed] = useState(false);
  const [isSkipRequested, setIsSkipRequested] = useState(false);
  const portalSchemeFallbackTimeoutRef = useRef(null);
  const hasSubmittedFeedbackRef = useRef(false);

  const primarySuccessCtaHref = useMemo(() => {
    const safeRedirectUrl = toSafeHttpUrl(searchParams.get('redirect_url'));
    return safeRedirectUrl || DEFAULT_BUNDLE_DOWNLOAD_URL;
  }, [searchParams]);
  const redirectUrlQueryValue = useMemo(() => toSafeHttpUrl(searchParams.get('redirect_url')), [searchParams]);
  const bundleId = useMemo(
    () => parsePositiveInteger(searchParams.get('bundle_id') ?? searchParams.get('bundleId')),
    [searchParams]
  );

  const isReadyByAnswer = Boolean(selectedSource);
  const isReadyByTimer = remainingSeconds <= 0;
  const isCtaReady = isReadyByAnswer || isReadyByTimer || isSkipRequested;

  useEffect(() => {
    if (isReadyByAnswer || isSkipRequested || remainingSeconds <= 0) {
      return undefined;
    }

    const timerId = window.setInterval(() => {
      setRemainingSeconds((currentSeconds) => {
        if (currentSeconds <= 1) {
          window.clearInterval(timerId);
          return 0;
        }
        return currentSeconds - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timerId);
    };
  }, [isReadyByAnswer, isSkipRequested, remainingSeconds]);

  useEffect(() => () => {
    if (portalSchemeFallbackTimeoutRef.current) {
      window.clearTimeout(portalSchemeFallbackTimeoutRef.current);
    }
  }, []);

  const submitFeedback = useCallback(() => {
    if (hasSubmittedFeedbackRef.current) {
      return;
    }
    hasSubmittedFeedbackRef.current = true;

    const source = resolveSurveySourceValue(selectedSource);
    const unlockMode = isSkipRequested ? 'skip' : (selectedSource ? 'selected_source' : 'timer');
    const settingsPayload = toSettingsPayload({
      source,
      unlockMode,
      bundleId,
      redirectUrl: redirectUrlQueryValue,
    });

    submitBundleSuccessSurveyToGoogle({
      source,
      unlockMode,
      bundleId,
      redirectUrl: redirectUrlQueryValue,
      user,
    }).catch((error) => {
      console.log('Error while submitting bundle success survey to Google', error);
    });

    meFn(settingsPayload).catch((error) => {
      console.log('Error while saving bundle success survey to me/settings', error);
    });
  }, [bundleId, isSkipRequested, redirectUrlQueryValue, selectedSource, user]);

  const handleSuccessDownloadClick = useCallback(async (event) => {
    if (!isCtaReady || isLaunchingPortal) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    submitFeedback();

    const fallbackToWebDownload = () => {
      window.location.href = primarySuccessCtaHref;
    };

    if (!isWindowsClient()) {
      fallbackToWebDownload();
      return;
    }

    setIsLaunchingPortal(true);

    const newlyRefreshedToken = await refreshSessionAndGetRefreshToken();
    if (!newlyRefreshedToken) {
      fallbackToWebDownload();
      setIsLaunchingPortal(false);
      return;
    }

    const portalSchemeUrl = `productioncrate-portal://google-auth?refresh_token=${encodeURIComponent(newlyRefreshedToken)}`;
    if (portalSchemeFallbackTimeoutRef.current) {
      window.clearTimeout(portalSchemeFallbackTimeoutRef.current);
    }

    portalSchemeFallbackTimeoutRef.current = window.setTimeout(() => {
      if (document.visibilityState === 'visible') {
        fallbackToWebDownload();
      }
      setIsLaunchingPortal(false);
    }, PORTAL_SCHEME_FALLBACK_DELAY_MS);

    try {
      window.location.href = portalSchemeUrl;
    } catch (error) {
      console.log('Error while redirecting to bundle portal scheme', error);
      fallbackToWebDownload();
      setIsLaunchingPortal(false);
    }
  }, [isCtaReady, isLaunchingPortal, primarySuccessCtaHref, submitFeedback]);

  const ctaWrapperTransform = isCtaReady
    ? 'translateY(0) scale(1) rotateX(0deg)'
    : 'translateY(16px) scale(0.9) rotateX(12deg)';
  const ctaButtonTransform = isCtaPressed ? 'scale(0.98)' : 'scale(1)';

  if (nativeAppClient && user) {
    return <NativeAppHandoff client={nativeAppClient} />;
  }

  return (
    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: '100vh', padding: '32px 16px', background: '#111217'}}>
      <div style={{maxWidth: '900px', width: '100%', background: '#1B1C24', border: '1px solid #2A2D39', borderRadius: '12px', padding: 'clamp(22px, 4vw, 34px) clamp(16px, 3vw, 28px)', color: '#F4F4F4', textAlign: 'center'}}>
        <div style={{display: 'flex', justifyContent: 'center', marginBottom: '20px'}}>
          <div style={{width: '84px', height: '84px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(27, 158, 88, 0.2)', border: '1px solid rgba(35, 201, 110, 0.55)'}}>
            <svg width="42" height="42" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M20 6L9 17L4 12" stroke="#44DA86" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        <h1 style={{margin: 0, marginBottom: '18px', fontSize: 'clamp(36px, 6vw, 48px)', lineHeight: '110%'}}>Purchase Success</h1>

        <p style={{margin: 0, marginBottom: '16px', color: '#F4F4F4', fontSize: 'clamp(17px, 2.75vw, 23px)', lineHeight: '130%'}}>How did you hear about the plugin?</p>

        <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px', textAlign: 'left'}}>
          {FIND_US_OPTIONS.map((option) => {
            const isSelected = selectedSource === option.label;
            return (
              <label
                key={option.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: `1px solid ${isSelected ? '#F36C17' : '#3A3E4F'}`,
                  background: isSelected ? 'rgba(243, 108, 23, 0.14)' : '#15171F',
                  color: '#F4F4F4',
                  fontSize: 'clamp(19px, 3.8vw, 22px)',
                  lineHeight: '145%',
                  boxSizing: 'border-box',
                  cursor: 'pointer'
                }}
              >
                <input
                  type="radio"
                  name="find-us-choice"
                  value={option.label}
                  checked={isSelected}
                  onChange={(event) => setSelectedSource(event.target.value)}
                  style={{width: '18px', height: '18px', accentColor: '#F36C17'}}
                />
                {option.icon ? <OptionLogo icon={option.icon} /> : null}
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>

        <div style={{position: 'relative', minHeight: '92px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          {!isCtaReady && (
            <button
              type="button"
              onClick={() => {
                setIsSkipRequested(true);
                setRemainingSeconds(0);
              }}
              style={{
                appearance: 'none',
                background: 'transparent',
                border: 0,
                color: 'rgba(244, 244, 244, 0.64)',
                fontSize: '14px',
                lineHeight: '20px',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                cursor: 'pointer',
                padding: '6px 10px'
              }}
            >
              Skip
            </button>
          )}

          <div
            aria-hidden={!isCtaReady}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              opacity: isCtaReady ? 1 : 0,
              pointerEvents: isCtaReady ? 'auto' : 'none',
              transform: ctaWrapperTransform,
              filter: isCtaReady ? 'blur(0px)' : 'blur(6px)',
              transition: 'opacity 420ms cubic-bezier(0.22, 1, 0.36, 1), transform 420ms cubic-bezier(0.22, 1, 0.36, 1), filter 420ms cubic-bezier(0.22, 1, 0.36, 1)'
            }}
          >
          <a
            href={primarySuccessCtaHref}
            onClick={handleSuccessDownloadClick}
            aria-disabled={!isCtaReady || isLaunchingPortal}
            onMouseDown={() => setIsCtaPressed(true)}
            onMouseUp={() => setIsCtaPressed(false)}
            onMouseLeave={() => setIsCtaPressed(false)}
            onTouchStart={() => setIsCtaPressed(true)}
            onTouchEnd={() => setIsCtaPressed(false)}
            onTouchCancel={() => setIsCtaPressed(false)}
            onPointerUp={submitFeedback}
            style={{
              padding: '16px 28px',
              borderRadius: '10px',
              background: isCtaReady && !isLaunchingPortal ? '#F36C17' : '#777B89',
              color: '#fff',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: 'clamp(24px, 4.8vw, 28px)',
              opacity: isCtaReady && !isLaunchingPortal ? 1 : 0.5,
              pointerEvents: isCtaReady && !isLaunchingPortal ? 'auto' : 'none',
              transform: ctaButtonTransform,
              boxShadow: '0 10px 24px rgba(243, 108, 23, 0.28)',
              transition: 'background-color 100ms ease-out, transform 100ms ease-out'
            }}
          >
            {isLaunchingPortal ? 'Opening...' : 'Download'}
          </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BundleCheckoutSuccessPoc;
